import { useState, type CSSProperties, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../api';
import { TurnstileWidget } from '../components/TurnstileWidget';
import { useClientConfig } from '../lib/useClientConfig';
import { useDocumentTitle } from '../lib/useDocumentTitle';

type Status = 'idle' | 'submitting' | 'sent' | 'error';

const linkButtonStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  padding: 0,
  color: 'inherit',
  font: 'inherit',
  textDecoration: 'underline',
  cursor: 'pointer',
};

export function ForgotPassword() {
  useDocumentTitle('Reset your password - Neomaris');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);

  // Turnstile (threat E1) — the request endpoint verifies it. Same runtime
  // site-key plumbing as signup; null in local dev / test means no widget.
  const {
    config: clientConfig,
    loaded: configLoaded,
    error: configError,
    reload: reloadConfig,
  } = useClientConfig();
  const turnstileSiteKey = clientConfig?.turnstileSiteKey ?? null;
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [turnstileWidgetKey, setTurnstileWidgetKey] = useState(0);
  const [turnstileFailed, setTurnstileFailed] = useState(false);

  const canSubmit =
    status !== 'submitting' &&
    configLoaded &&
    !configError &&
    (turnstileSiteKey === null || turnstileToken !== null);

  function retryTurnstile() {
    setTurnstileFailed(false);
    setTurnstileWidgetKey((k) => k + 1);
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!configLoaded || configError) return;
    if (turnstileSiteKey !== null && turnstileToken === null) return;
    setStatus('submitting');
    setError(null);
    try {
      const body: { email: string; 'cf-turnstile-response'?: string } = { email };
      if (turnstileToken !== null) body['cf-turnstile-response'] = turnstileToken;
      await api('/v1/auth/password-reset/request', { method: 'POST', body });
      // A2: the response is identical whether or not an account exists, so
      // the UI is deliberately non-committal.
      setStatus('sent');
    } catch (err) {
      const e = err as { body?: { message?: string }; message?: string };
      // Single-use token consumed by this attempt — reset the widget.
      if (turnstileSiteKey !== null) {
        setTurnstileToken(null);
        setTurnstileNonce((n) => n + 1);
      }
      setStatus('error');
      setError(e.body?.message ?? e.message ?? 'Something went wrong. Please try again.');
    }
  }

  if (status === 'sent') {
    return (
      <section className="auth-panel">
        <div className="auth-card auth-card-v2">
          <div className="auth-state">
            <div className="auth-state-icon auth-state-icon--ok">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M4 6.5 12 12l8-5.5" strokeLinecap="round" strokeLinejoin="round" />
                <rect x="3" y="5" width="18" height="14" rx="2.5" />
              </svg>
            </div>
            <h2>Check your email</h2>
            <p className="auth-sub" role="status" data-testid="reset-sent">
              If an account exists for <strong>{email}</strong>, we’ve sent a link to set a new
              password. The link is valid for 60 minutes — check your spam folder if you don’t see
              it.
            </p>
            <Link to="/login" className="btn btn-outline auth-submit">
              Back to sign in
            </Link>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="auth-panel">
      <div className="auth-card auth-card-v2">
        <h2>Reset your password</h2>
        <p className="auth-sub">
          Enter your account email and we’ll send you a link to set a new password.
        </p>
        <form onSubmit={onSubmit} data-testid="forgot-form">
          {error !== null ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="error-banner">
                <li>{error}</li>
              </ul>
            </div>
          ) : null}
          {configError ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="config-error-banner">
                <li>
                  We couldn’t load verification.{' '}
                  <button type="button" style={linkButtonStyle} onClick={reloadConfig}>
                    Retry
                  </button>
                </li>
              </ul>
            </div>
          ) : null}
          <div className="form-group">
            <label htmlFor="forgot-email">Email</label>
            <input
              type="email"
              id="forgot-email"
              className="form-input"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          {turnstileSiteKey !== null && !turnstileFailed ? (
            <div className="form-group">
              <TurnstileWidget
                key={turnstileWidgetKey}
                siteKey={turnstileSiteKey}
                onToken={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileFailed(true)}
                resetSignal={turnstileNonce}
              />
            </div>
          ) : null}
          {turnstileFailed ? (
            <div className="auth-banner auth-banner--error" role="alert">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                aria-hidden="true"
              >
                <path d="M12 9v4" strokeLinecap="round" />
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z"
                  strokeLinejoin="round"
                />
                <circle cx="12" cy="17" r="0.6" fill="currentColor" stroke="none" />
              </svg>
              <ul className="form-errors" data-testid="turnstile-error-banner">
                <li>
                  We couldn’t finish a quick security check — usually a weak connection.{' '}
                  <button type="button" style={linkButtonStyle} onClick={retryTurnstile}>
                    Retry
                  </button>
                  , or try again in a moment.
                </li>
              </ul>
            </div>
          ) : null}
          <button type="submit" className="btn btn-primary auth-submit" disabled={!canSubmit}>
            {status === 'submitting' ? 'Sending…' : 'Send reset link'}
          </button>
        </form>
        <p className="auth-foot">
          Remembered it? <Link to="/login">Back to sign in</Link>
        </p>
      </div>
    </section>
  );
}
